import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  GROUP_BY_APP_VALUE,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import Tile from '../Tile';

import {
  getDynamicStyles,
  getNodeDataToViewForTiles,
  getTotalChildrenCount,
  removeChildren,
} from './utils';

import './Cards.css';

function Cards({
  data,
  level,
  handleSidePanel,
  handleAutoSelectParent,
  setMergedLists,
  actionPopoverOpenId,
  setActionPopoverOpenId,
  isScrolling,
  sharedColumnWidths,
  onSharedColumnResize,
  parentIndex = null,
  onTitleExpand,
}) {
  const [colsToShow, setColsToShow] = useState({});
  const [disableTransition, setDisableTransition] = useState(false);
  const [treeStyles, setTreeStyles] = useState({});
  const [shouldGetStyles, setShouldGetStyles] = useState(false);
  const [columnWidths, setColumnWidths] = useState({}); // Stores column width adjustments for resize feature
  const [expandedTiles, setExpandedTiles] = useState({}); // Tracks which tiles are expanded
  const allCardsRef = useRef(null);

  const isTopLevel = level === 0 && !sharedColumnWidths; // Top level manages column widths, child levels inherit

  const {
    core: { isLoading, campaignType },
    global: { mediaQueryValue },
    sidePanel: { isSidePanelVisible },
    viewControls: { groupByOptions, colsFilters },
  } = useSelector((state) => state?.certifications?.details);

  const isLevelZero = level === 0;
  const isLevelOne = level === 1;
  const selectedGroupByValue = groupByOptions?.find(
    (option) => option.isChecked
  )?.value;
  const isUsingGroupBy = selectedGroupByValue === GROUP_BY_APP_VALUE;

  const mobileXLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXLargeQuery.value <= mediaQueryValue;

  const cardsClasses = classnames(
    'Cards',
    `Cards--${level}`,
    isUsingGroupBy && 'Cards--isUsingGroupBy',
    isSidePanelVisible && 'Cards--isSidePanelVisible'
  );

  // Remove transitions from Card trees for durations of transition after loading
  useEffect(() => {
    let timer;

    if (isLoading) {
      setDisableTransition(true);
    } else {
      timer = setTimeout(() => {
        setDisableTransition(false);
      }, 800);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.target.classList.contains('Cards-tree--isBeingRemoved')
        ) {
          const parent = mutation.target.closest('.Cards-tree--0');
          const subParent = mutation.target.closest('.Cards-tree--1');

          if (
            parent &&
            !parent.classList.contains('Cards-tree--isBeingRemoved')
          )
            parent.style.height = 'auto';

          if (
            subParent &&
            !subParent.classList.contains('Cards-tree--isBeingRemoved')
          )
            parent.style.height = 'auto';
        }
      });
    });

    const config = {
      attributes: true,
      subtree: true,
      attributeFilter: ['class'],
    };

    if (allCardsRef.current) {
      observer.observe(allCardsRef.current, config);
      setShouldGetStyles(true);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (colsFilters) setColsToShow(colsFilters);
  }, [colsFilters]);

  // Reset column widths when groupBy changes
  useEffect(() => {
    if (isTopLevel) setColumnWidths({});
  }, [isUsingGroupBy, isTopLevel]);

  const getTreeClasses = (node, treeIndex) => {
    const isTreeIndexZero = treeIndex === 0;
    const isTreeIndexEnd = treeIndex === data.length - 1;
    const isEnt = node.CERT_ENTITLEMENT_VALUEKEY <= 0;
    const hasNodeChildren = node?.children?.length > 0;

    const isTopTile = isTreeIndexZero && !isLevelZero;
    const isBottomTile = isTreeIndexEnd && !isLevelZero;
    const hasEntChild = isLevelOne && isEnt && hasNodeChildren;

    const defaultTreeClasses = classnames(
      'Cards-tree',
      `Cards-tree--${level}`,
      `Cards-treeIndex--${treeIndex}`,
      isTopTile && 'Cards-tree--isTopTile',
      isBottomTile && 'Cards-tree--isBottomTile',
      hasEntChild && 'Cards-tree--hasEntChild',
      !hasNodeChildren && 'Cards-tree--hasNoChildren'
    );

    return defaultTreeClasses;
  };

  const getTreeStyles = (node) => {
    const { count: totalChildrenCount, levelOneChildrenWithChildren } =
      getTotalChildrenCount(node, level);

    const dynamicTreeStyles = getDynamicStyles(
      totalChildrenCount,
      levelOneChildrenWithChildren,
      level,
      isMobileXlargeWidth,
      isLevelZero && isUsingGroupBy
    );

    return dynamicTreeStyles;
  };

  useEffect(() => {
    if (!data) return;

    const updateTreeStyles = () => {
      const newTreeStyles = data.reduce((acc, node, index) => {
        const dynamicTreeStyles = getTreeStyles(node);

        acc[index] = dynamicTreeStyles;

        return acc;
      }, {});

      setTreeStyles(newTreeStyles);
    };

    updateTreeStyles();
  }, [data, shouldGetStyles, isMobileXlargeWidth]);

  // Use local widths at top level, shared widths for nested levels
  const activeColumnWidths = isTopLevel
    ? columnWidths
    : sharedColumnWidths || {};

  // Handles column resize events from Tile components
  const handleColumnResize = useCallback(
    (colIndex, width, delta) => {
      if (isTopLevel) {
        // Store delta for title column (index 0), absolute width for attribute columns
        setColumnWidths((prev) => ({
          ...prev,
          [colIndex]: colIndex === 0 ? delta : width,
        }));
      }

      // Propagate resize to parent if callback provided
      if (onSharedColumnResize)
        onSharedColumnResize(colIndex, colIndex === 0 ? delta : width, delta);
    },
    [isTopLevel, onSharedColumnResize]
  );

  // Handles title expansion from Tile components
  const handleTitleExpand = useCallback(
    (tileId, isExpanded) => {
      setExpandedTiles((prev) => ({
        ...prev,
        [tileId]: isExpanded,
      }));

      if (onTitleExpand) onTitleExpand(tileId, isExpanded);
    },
    [onTitleExpand]
  );

  if (!data) return null;

  return (
    <div className={cardsClasses} ref={allCardsRef}>
      {data?.map((node, index) => {
        const nodeData = removeChildren(node);
        const filteredNodeData = getNodeDataToViewForTiles(
          nodeData,
          colsToShow,
          campaignType
        );
        const treeClassNames = getTreeClasses(node, index);
        const customWidth = activeColumnWidths[node?.id];

        const isExpanded = expandedTiles[node?.id];
        const expandedHeight = isExpanded && isMobileXlargeWidth ? 4 : 0;

        return (
          <div
            id={node?.id}
            key={node?.tracker?.id}
            style={{
              ...treeStyles[index],
              transition: disableTransition ? 'none' : '',
              ...(customWidth && { width: `calc(100% + ${customWidth}px)` }),
              ...(expandedHeight > 0 && {
                height: `calc(${
                  treeStyles[index]?.height || '4rem'
                } + ${expandedHeight}rem)`,
              }),
            }}
            className={treeClassNames}>
            <Tile
              data={filteredNodeData}
              nodeData={nodeData}
              level={level}
              handleSidePanel={handleSidePanel}
              handleAutoSelectParent={handleAutoSelectParent}
              setMergedLists={setMergedLists}
              actionPopoverOpenId={actionPopoverOpenId}
              setActionPopoverOpenId={setActionPopoverOpenId}
              isScrolling={isScrolling}
              columnWidths={activeColumnWidths}
              onResizeColumn={handleColumnResize}
              showResizeHandles={level === 0 || (isUsingGroupBy && level === 1)} // Show handles on first tile/group only
              isFirstTile={index === 0}
              isFirstGroup={
                (level === 0 && index === 0) ||
                (level === 1 && index === 0 && parentIndex === 0)
              }
              tileLevel={level}
              isGroupByActive={isUsingGroupBy}
              onTitleExpand={handleTitleExpand}
            />
            {node?.children?.length !== 0 && (
              <Cards
                data={node?.children || []}
                level={level + 1}
                handleSidePanel={handleSidePanel}
                handleAutoSelectParent={handleAutoSelectParent}
                setMergedLists={setMergedLists}
                actionPopoverOpenId={actionPopoverOpenId}
                setActionPopoverOpenId={setActionPopoverOpenId}
                isScrolling={isScrolling}
                sharedColumnWidths={activeColumnWidths}
                onSharedColumnResize={handleColumnResize}
                parentIndex={level === 0 ? index : parentIndex}
                onTitleExpand={handleTitleExpand}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

Cards.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  level: PropTypes.number,
  handleSidePanel: PropTypes.func.isRequired,
  handleAutoSelectParent: PropTypes.func.isRequired,
  setMergedLists: PropTypes.func.isRequired,
  actionPopoverOpenId: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setActionPopoverOpenId: PropTypes.func.isRequired,
  isScrolling: PropTypes.bool.isRequired,
  sharedColumnWidths: PropTypes.shape({}),
  onSharedColumnResize: PropTypes.func,
  parentIndex: PropTypes.number,
  onTitleExpand: PropTypes.func,
};

Cards.defaultProps = {
  level: 0,
  actionPopoverOpenId: false,
  sharedColumnWidths: null,
  onSharedColumnResize: null,
  parentIndex: null,
  onTitleExpand: null,
};

export default React.memo(Cards);
